import { nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useNuxtApp } from '#imports';

const entries = new Map<string, { top: number; focusIndex: number; focusKey?: string }>();
const pathKey = (path: string) => path.replace(/\/$/, '');

/** Default layout scrolls inside a div, outside Vue Router's window restoration. */
export function usePageReadingPosition(path: () => string, options: { resetOnQueryChange?: boolean; ready?: () => boolean; focusHeading?: boolean; focusKeyAttribute?: string } = {}) {
  const pageRoot = ref<HTMLElement | null>(null);
  const pageHeading = ref<HTMLElement | null>(null);
  const router = useRouter();
  const nuxtApp = useNuxtApp();
  let entryKey = '';
  let active = true;
  let version = 0;
  let pendingEntry = true;
  const container = () => pageRoot.value?.closest<HTMLElement>('[data-musicmint-page-scroll]');
  const focusItems = () => Array.from(pageRoot.value?.querySelectorAll<HTMLElement>(`a, summary, button, input, select, textarea, [tabindex="0"]${options.focusKeyAttribute ? `, [${options.focusKeyAttribute}]` : ''}`) || []);
  const focusKey = (element: HTMLElement | null) => options.focusKeyAttribute && element
    ? element.getAttribute(options.focusKeyAttribute) || element.id || undefined
    : undefined;
  const navigationChanged = (to: { path: string; fullPath?: string }, from: { path: string; fullPath?: string }) =>
    options.resetOnQueryChange ? (to.fullPath || to.path) !== (from.fullPath || from.path) : to.path !== from.path;

  const enter = async (restoreFocus = false) => {
    const request = ++version;
    await nextTick();
    if (!active || request !== version || options.ready?.() === false || !pageRoot.value?.isConnected || pathKey(router.currentRoute.value.path) !== pathKey(path())) return;
    if (!pendingEntry) {
      if (restoreFocus && options.focusHeading !== false && document.activeElement === document.body && pageHeading.value?.getClientRects().length) pageHeading.value.focus({ preventScroll: true });
      return;
    }
    // Keep the entry captured here: popstate updates history before leave guards.
    const routeKey = options.resetOnQueryChange ? (router.currentRoute.value.fullPath || pathKey(path())) : pathKey(path());
    entryKey = `${router.options.history.state.position ?? 'route'}:${routeKey}`;
    const saved = entries.get(entryKey);
    const scroll = container();
    if (!scroll) return;
    pendingEntry = false;
    scroll.scrollTop = saved?.top ?? 0;
    const items = focusItems();
    const focus = saved
      ? saved.focusKey ? items.find(item => focusKey(item) === saved.focusKey) : items[saved.focusIndex]
      : options.focusHeading === false ? null : pageHeading.value;
    if (focus?.getClientRects().length) focus.focus({ preventScroll: true });
  };

  const stopCapture = router.beforeEach((to, from) => {
    if (!active || pathKey(from.path) !== pathKey(path()) || !navigationChanged(to, from)) return;
    const scroll = container();
    if (!scroll || !entryKey) return;
    entries.delete(entryKey);
    entries.set(entryKey, { top: scroll.scrollTop, focusIndex: focusItems().indexOf(document.activeElement as HTMLElement), focusKey: focusKey(document.activeElement as HTMLElement) });
    if (entries.size > 64) entries.delete(entries.keys().next().value!);
  });
  const stopNavigation = router.afterEach((to, from, failure) => {
    if (!failure && pathKey(to.path) === pathKey(path()) && navigationChanged(to, from)) {
      pendingEntry = true;
      if (from.path === to.path) void enter();
    }
    if (failure || !active || pathKey(from.path) !== pathKey(path()) || !navigationChanged(to, from)) return;
    const scroll = container();
    if (scroll) scroll.scrollTop = 0;
  });
  // Cached pages can activate before their DOM is reattached during out-in transitions.
  const stopPageFinish = nuxtApp.hook('page:finish', () => enter());
  const stopTransitionFinish = nuxtApp.hook('page:transition:finish', () => enter());
  onMounted(enter);
  onActivated(() => { active = true; pendingEntry = true; void enter(); });
  onDeactivated(() => { active = false; version++; });
  onBeforeUnmount(() => { active = false; version++; stopCapture(); stopNavigation(); stopPageFinish(); stopTransitionFinish(); });
  return { pageRoot, pageHeading, restorePending: () => enter(), restoreAfterLoad: () => enter(true) };
}
